import { HttpHeaders, HttpErrorResponse } from '@angular/common/http';

export const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export const safeNumber = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const newId = (prefix: string): string =>
  `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;

export const newTreeId = (): string =>
  Math.random().toString(16).slice(2) + Date.now().toString(16);

export const deepClone = <T>(x: T): T => JSON.parse(JSON.stringify(x));

export const escapeHtml = (s: string): string =>
  (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const normalizeError = (err: any): string => {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err?.message) return String(err.message);
  try { return JSON.stringify(err); } catch { return 'Unknown error'; }
};

export const isJsonLike = (text: string | null | undefined): boolean => {
  const t = (text ?? '').toString().trim();
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
};

export const isXmlLike = (text: string | null | undefined): boolean => {
  const t = (text ?? '').toString().trim();
  if (!t) return false;
  if (!t.startsWith('<') || !t.endsWith('>')) return false;
  return true;
};

export const httpHeadersToDict = (h: HttpHeaders | null | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!h) return out;
  for (const k of h.keys()) {
    const all = h.getAll(k);
    out[k] = all && all.length ? all.join(', ') : (h.get(k) ?? '');
  }
  return out;
};

export const headersDictToText = (headers: Record<string, string>): string =>
  Object.keys(headers)
    .sort((a, b) => a.localeCompare(b))
    .map(k => `${k}: ${headers[k]}`)
    .join('\n');

export const extractErrorBodyText = (httpErr: HttpErrorResponse): string => {
  const e = httpErr.error as any;

  if (typeof e === 'string') return e;
  if (e && typeof e === 'object' && !(e instanceof Blob)) {
    try { return JSON.stringify(e, null, 2); } catch { return String(e); }
  }
  if (e instanceof Blob) {
    return `[binary error body] (${e.type || 'application/octet-stream'}, ${e.size} bytes)`;
  }
  return (httpErr.message ?? 'Request failed');
};

export const mergeKv = (
  existing: Array<{ key: string; value: string }>,
  add: Array<{ key: string; value: string }>
): Array<{ key: string; value: string }> => {
  const map = new Map<string, { key: string; value: string }>();
  for (const x of existing ?? []) {
    const k = (x.key ?? '').trim();
    if (!k) continue;
    map.set(k.toLowerCase(), { key: x.key, value: x.value });
  }
  for (const x of add ?? []) {
    const k = (x.key ?? '').trim();
    if (!k) continue;
    if (!map.has(k.toLowerCase())) map.set(k.toLowerCase(), { key: x.key, value: x.value });
  }
  return Array.from(map.values());
};

export const parsePathSegments = (path: string): Array<string | number> => {
  const p = (path ?? '').trim();
  if (!p) return [];
  const segs: Array<string | number> = [];
  const re = /([^[.\]]+)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(p)) !== null) {
    if (m[1] != null) segs.push(m[1]);
    else if (m[2] != null) segs.push(Number(m[2]));
  }
  return segs;
};

export const getByPath = (obj: any, path: string): any => {
  const segs = parsePathSegments(path);
  let cur = obj;
  for (const s of segs) {
    if (cur == null) return undefined;
    cur = cur[s as any];
  }
  return cur;
};

export const prettyXml = (xml: string): string => {
  const input = (xml ?? '').toString().trim();
  if (!input) return input;

  // Add newlines between tags
  const withBreaks = input.replace(/(>)(<)(\/*)/g, '$1\n$2$3');

  const lines = withBreaks.split('\n').map(l => l.trim()).filter(l => l.length > 0);

  const INDENT = '  ';
  let level = 0;

  const isClosing = (l: string) => /^<\/[^>]+>/.test(l);
  const isSelfClosing = (l: string) => /^<[^>]+\/>$/.test(l);
  const isDeclarationOrComment = (l: string) =>
    /^<\?xml\b/.test(l) || /^<!DOCTYPE\b/i.test(l) || /^<!--/.test(l) || /^<!\[CDATA\[/.test(l);

  // <tag>text</tag> or <tag><inner/></tag> on one line
  const isOpenAndCloseSameLine = (l: string) =>
    /^<[^!?\/][^>]*>.*<\/[^>]+>$/.test(l);

  const isOpening = (l: string) =>
    /^<[^!?\/][^>]*>$/.test(l) && !isClosing(l) && !isSelfClosing(l);

  const out: string[] = [];

  for (const line of lines) {
    if (isDeclarationOrComment(line)) {
      out.push(INDENT.repeat(level) + line);
      continue;
    }

    if (isClosing(line)) {
      level = Math.max(0, level - 1);
      out.push(INDENT.repeat(level) + line);
      continue;
    }

    if (isOpenAndCloseSameLine(line) || isSelfClosing(line)) {
      out.push(INDENT.repeat(level) + line);
      continue;
    }

    // Opening tag on its own line
    out.push(INDENT.repeat(level) + line);
    if (isOpening(line)) level += 1;
  }

  return out.join('\n');
};
